/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import dynamic from 'next/dynamic'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { addDays, differenceInDays, subDays, subMinutes } from 'date-fns'

import { selectMenuIsOpen } from '~/redux/slices/menu'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectStoryCardId } from '~/redux/slices/story-card'
import { QUIZ_SUBJECT } from '~/types/homebaseQuiz.types'
import { selectEvents, selectFunfact, hasLeveledUp } from '~/gamification/slices/gamification.slice'
import { EventStatus, REWARD_FEATURE } from '~/gamification/types/gamification.types'
import {
  selectIsCampaignModalContent,
  selectShowFeedbackModal,
  setShowFeedbackModalCreator,
  setShowAndroidAppModalCreator,
  selectShowAndroidAppModal,
} from '~/redux/slices/modals'
import useCurrentUser from '~/hooks/useCurrentUser'
import authService from '~/services/auth.service'
import { getLoggedInSeconds } from '~/auth/utils/register.utils'
import { selectQuestState } from '~/redux/slices/quest'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import { selectShowGemShop } from '~/redux/slices/gem-shop'
import { selectImageModalOpen, selectIsChatAiOpen } from '~/redux/slices/chat-ai'
import { useGetRewardsQuery, useRewardStatsQuery, useSetChecklistRewardsMutation } from '~/redux/apis/reward-api'
import { notificationApi } from '~/redux/apis/notification-api'
import { ModalType } from '~/components/Modals/GamificationModal/GamificationModal.types'
import { selectHomebaseIsOpen } from '~/redux/slices/homebase'
import { getChecklistNotification, unsetChecklistNotification } from '~/redux/slices/checklist'
import { selectIsSubscriptionApplicable, setSubscriptionCoupon } from '~/redux/slices/subscription.slice'
import { useGetDiscountCouponQuery } from '~/redux/apis/payment-api'
import { StripeSubscriptionStatus } from '~/types/stripe-subscription'
import { isAuthenticated, selectUserId } from '~/auth/slices/auth.slice'
import { useChecklistItem } from '~/hooks/useChecklist'
import ChatAiImageModal from '~/aioracle/ChatAiImageModal'
import { leaderboardApi } from '~/Leaderboard/apis/leaderboard-api'
import checklistAnalytics from '~/analytics/checklist/checklistAnalytics'
import ROUTES from '~/shared/constants/routes.const'
import { getShowUserFeedbackModal } from '~/user-feedback/redux/user-feedback'
import userFeedbackAnalytics from '~/analytics/userFeedback/userFeedbackAnalytics'
import appConfig from '~/config/app'
import { MobileAppSubscriptionStatus } from '~/types/user-info-types'
import GetAndroidAppModal from '~/components/GetAndroidAppModal'

import { selectCurriculumChangeWarning, selectShouldShowCertificateModal } from '~/curriculum/slice/curriculum.slice'
import CurriculumChangeWarningModal from '~/curriculum/components/CurriculumChangeWarningModal'
import StateSwitchModal from '~/curriculum/components/StateSwitchModal'
import CurriculumCertificateClaimModal from '~/curriculum/components/CurriculumCertificateClaimModal'
import useSubscriptionIsRequired from '~/subscription/hooks/useSubscriptionIsRequired'
import useDeferredPaywallTrigger from '~/subscription/hooks/useDeferredPaywallTrigger'
import { userHasSubscription } from '~/redux/utils/subscription'

import AccessExpiredMobileApp from '~/components/Modals/AccessExpiredMobileApp'

import ProgressMigrationFlow from './Modals/ProgressMigrationFlow'

import { useHasLevelUp } from './Modals/GamificationModal/hooks/useHasLevelUp'
import { SubscriptionModalV1 } from './Modals/SubscriptionModalV1'
import ReferralModal from './Referral/ReferralModal'

const ModalStoryCard = dynamic(() => import('~/components/Modals/ModalStoryCard'), { ssr: false })
const FunfactModal = dynamic(() => import('./Modals/FunfactModal'), { ssr: false })
const HomebaseModal = dynamic(() => import('~/homebase/HomebaseModal'), { ssr: false })

const QuestFinish = dynamic(() => import('~/scenarios/quest/components/QuestFinish/QuestFinish'), { ssr: false })
const SSOCampaignCard = dynamic(() => import('~/components/SSOCampaignCard'), {
  ssr: true,
})
const TypeformFeedback = dynamic(() => import('~/components/Typeform/TypeformFeedback'), { ssr: false })
const GamificationModal = dynamic(() => import('./Modals/GamificationModal/GamificationModal'), { ssr: false })
const GemShop = dynamic(() => import('~/components/Modals/GemShop/GemShop'), { ssr: false })

const PilotCountryCouponModal = dynamic(() => import('~/components/Modals/PilotCountryCouponModal'), {
  ssr: false,
})

const UserFeedbackModal = dynamic(() => import('~/user-feedback/UserFeedback'), { ssr: false })

// The auto-triggered referral modal is suppressed on these route prefixes: signup/onboarding funnels,
// auth flows, payment/checkout, family flows, the referral page itself, campaigns/partner pages,
// staff/ops panels, and immersive/assessment flows (live tutoring sessions, exam/test flow).
const REFERRAL_MODAL_EXCLUDED_ROUTES = [
  ROUTES.ONBOARDING,
  ROUTES.REGISTER,
  ROUTES.GET_STARTED,
  ROUTES.START,
  ROUTES.LOGIN,
  ROUTES.RESET_PASSWORD,
  ROUTES.RECOVER_PASSWORD,
  ROUTES.EMAIL_VERIFY,
  ROUTES.AUTH_CALLBACK,
  ROUTES.SUBSCRIPTION_PAGE,
  ROUTES.BOOKING_CHECKOUT,
  ROUTES.ADYEN_CALLBACK_PAGE,
  ROUTES.VIPPS_CALLBACK_PAGE,
  ROUTES.MATHMAGIC_DONATE,
  ROUTES.FAMILY,
  ROUTES.REFERRAL,
  ROUTES.CAMPAIGNS,
  ROUTES.PARTNER,
  ROUTES.ADMIN_PANEL,
  ROUTES.TUTOR_PANEL,
  ROUTES.DEV,
  ROUTES.PROFILES,
  ROUTES.SESSION_PAGE,
  ROUTES.TEST_PREP,
]

function useTrophyQuestId(): QUIZ_SUBJECT | undefined {
  const events = useAppSelector(selectEvents)

  const trophyQuest = events?.find((event) => event.id && event.status === EventStatus.TROPHY_DIALOGUE)

  return trophyQuest?.id as QUIZ_SUBJECT | undefined
}

export function useTypeformId(): string {
  const router = useRouter()
  const locale = router.locale
  if (locale === 'no' && process.env.NEXT_PUBLIC_TYPEFORM_ID_NO) {
    return process.env.NEXT_PUBLIC_TYPEFORM_ID_NO
  }
  return process.env.NEXT_PUBLIC_TYPEFORM_ID ?? ''
}

function useShowMobileAppPopup(): boolean {
  const { asPath: path } = useRouter()
  const authenticated = useAppSelector(isAuthenticated)
  const onboardingUrls =
    path.includes('/checkout') ||
    path.includes('/get-started') ||
    path.includes('/subscription') ||
    path.includes('/campaigns/accenture')

  const showAndroidModal = useAppSelector(selectShowAndroidAppModal)
  const notMobileApp = !appConfig.isMobileApp

  // Soft Delete Android Modal for now, can be re-enabled when we have the right content to show in the modal and want to target Android users again
  return false // !authenticated && !onboardingUrls && notMobileApp && showAndroidModal
}

function useShowTypeform(): boolean {
  const { asPath: path } = useRouter()
  const showFeedbackModal = useAppSelector(selectShowFeedbackModal)
  const authenticated = useAppSelector(isAuthenticated)
  const user = useCurrentUser()
  const createdAt = user?.createdAt
  const dispatch = useAppDispatch()

  const menuIsOpen = useAppSelector(selectMenuIsOpen)
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })

  const oracleIsOpen = useAppSelector(selectIsChatAiOpen)
  const typeformId = useTypeformId()

  const [lastTypeformFeedbackInteractionTime, setLastTypeformFeedbackInteractionTime] = useState<number>(0)

  useEffect(() => {
    if (lastTypeformFeedbackInteractionTime) return
    const interactionTime = localStorage.getItem(typeformId)
    setLastTypeformFeedbackInteractionTime(interactionTime ? Number(interactionTime) : 0)
  }, [])

  const dateFilterRewardStatsQuery = useMemo(() => {
    return {
      startDate: subDays(new Date(), 30).toISOString(),
      endDate: addDays(new Date(), 1).toISOString(),
    }
  }, [])

  const typeformCondition =
    // The Typeform embed ships user_id, email, name and country to a third party as hidden fields.
    !appConfig.isSchoolPlatform &&
    Boolean(typeformId) &&
    authenticated &&
    (!isMobile || !oracleIsOpen) &&
    (!isMobile || !menuIsOpen) &&
    !path.includes('/games/multgame') &&
    !path.includes('/games/stacknload') &&
    !path.includes('/math-vault') &&
    !path.includes('/avatar-multgame') &&
    !path.includes('/avatar') &&
    !path.includes('/subscription') &&
    !path.includes('/onboarding') &&
    !path.includes('/adyen') &&
    !path.includes('/activity/') &&
    !path.includes('/campaigns/') &&
    path !== '/' &&
    createdAt &&
    new Date(createdAt) <= (appConfig.isProdEnvironment ? subDays(new Date(), 1) : subMinutes(new Date(), 5)) &&
    differenceInDays(new Date(), lastTypeformFeedbackInteractionTime) > 90
  const { data: rewardStats } = useRewardStatsQuery(dateFilterRewardStatsQuery, { skip: !typeformCondition })

  const userHasEarnedXp = rewardStats && rewardStats?.xp && rewardStats?.xp > 0

  const LOGGED_IN_SECONDS_LIMIT = 10

  useEffect(() => {
    if (!typeformId) return

    const showTypeform = (): void => {
      dispatch(setShowFeedbackModalCreator(true))
      userFeedbackAnalytics.logTypeformSeen(typeformId)
      localStorage.setItem(`${typeformId}:seen`, String(new Date().getTime()))
    }

    const answeredKey = typeformId ?? 'missing_typeform_modal'
    const answeredTypeformModal = localStorage.getItem(answeredKey)
    const answerTime = answeredTypeformModal ? new Date(Number(answeredTypeformModal)) : undefined
    if ((answerTime && differenceInDays(new Date(), answerTime) < 30) || !typeformCondition) {
      dispatch(setShowFeedbackModalCreator(false))
    } else if (user) {
      const loggedInSeconds = getLoggedInSeconds(user)
      if (typeof loggedInSeconds === 'undefined') {
        dispatch(setShowFeedbackModalCreator(false))
      } else if (!showFeedbackModal && loggedInSeconds < LOGGED_IN_SECONDS_LIMIT) {
        const timeoutId = setTimeout(() => {
          showTypeform()
        }, (LOGGED_IN_SECONDS_LIMIT + 1 - loggedInSeconds) * 1000)

        return () => {
          clearTimeout(timeoutId)
        }
      } else if (!showFeedbackModal && loggedInSeconds >= LOGGED_IN_SECONDS_LIMIT) {
        showTypeform()
      }
    }
  }, [typeformCondition, dispatch, showFeedbackModal, user, typeformId, userHasEarnedXp])
  return showFeedbackModal && !!userHasEarnedXp
}

export default function Modals(): React.ReactElement {
  const [hasPopUpCoupon, setHasPopUpCoupon] = useState<null | string>()
  const [showPopUpCoupon, setShowPopUpCoupon] = useState<boolean>(false)
  const storyCardId = useAppSelector(selectStoryCardId)
  const trophyQuestId = useTrophyQuestId()
  const homebaseIsOpen = useAppSelector(selectHomebaseIsOpen)
  const isImageModalOpen = useAppSelector(selectImageModalOpen)
  const funfact = useAppSelector(selectFunfact)
  const campaignContent = useAppSelector(selectIsCampaignModalContent)
  const showTypeform = useShowTypeform()
  const questState = useAppSelector(selectQuestState)
  const showLevelUp = useAppSelector(hasLeveledUp)
  const showGemShop = useAppSelector(selectShowGemShop)
  const showUserFeedbackModal = useAppSelector(getShowUserFeedbackModal)
  const showCertificateModal = useAppSelector(selectShouldShowCertificateModal)
  const showCurriculumChangeWarning = useAppSelector(selectCurriculumChangeWarning)
  const user = useCurrentUser()
  const subscriptionApplicable = useAppSelector(selectIsSubscriptionApplicable)
  const subscriptionActive =
    user?.stripeSubscription[0]?.subscriptionStatus === StripeSubscriptionStatus.ACTIVE ||
    user?.stripeSubscription[0]?.subscriptionStatus === StripeSubscriptionStatus.TRIALING ||
    user?.stripeSubscription[0]?.subscriptionStatus === StripeSubscriptionStatus.PAST_DUE
  const hasActiveMobileSubscription =
    user?.validMobileAppSubscription?.subscriptionStatus === MobileAppSubscriptionStatus.ACTIVE ||
    user?.validMobileAppSubscription?.subscriptionStatus === MobileAppSubscriptionStatus.TRIALING
  const rewards = useGetRewardsQuery(undefined, { skip: !user })
  const authenticated = useAppSelector(isAuthenticated)
  const [showGuestSubModal, setShowGuestSubModal] = useState(false)
  const isMobileApp = appConfig.isMobileApp

  useEffect(() => {
    // Redux auth lags the stored token on first paint, so !authenticated alone flashes at signed-in users
    setShowGuestSubModal(!authenticated && !authService.isAuthenticated())
  }, [authenticated])

  const rewardFeature = useAppSelector(getChecklistNotification) as REWARD_FEATURE
  const { data: coupon } = useGetDiscountCouponQuery(undefined, {
    skip: !subscriptionApplicable || subscriptionActive || !authenticated || hasActiveMobileSubscription,
  })
  const { isSubscriptionRequired } = useSubscriptionIsRequired()
  const hasValidSubscription = userHasSubscription(user)

  const userId = useAppSelector(selectUserId)
  const [setRewards] = useSetChecklistRewardsMutation()
  const checklistItem = useChecklistItem(rewardFeature)
  const router = useRouter()
  const isOnboarding =
    router.pathname.startsWith(ROUTES.ONBOARDING) ||
    router.pathname.startsWith(ROUTES.REGISTER) ||
    router.pathname.startsWith(ROUTES.SUBSCRIPTION_PAGE)

  const shouldShowReferralModal = user?.shouldShowReferralModal
  const [showReferralModal, setShowReferralModal] = useState(false)

  // Exact-segment match so e.g. '/tutor' does not exclude '/tutor-on-demand'
  const isReferralModalExcludedPath = REFERRAL_MODAL_EXCLUDED_ROUTES.some(
    (path) => router.pathname === path || router.pathname.startsWith(`${path}/`),
  )
  // Referral is for subscribers only: never show it to non-subscribers or users
  // from non-subscription countries
  const isReferralModalApplicableUser = hasValidSubscription && Boolean(user?.isSubscriptionApplicable)

  useEffect(() => {
    const shouldShow = Boolean(shouldShowReferralModal && isReferralModalApplicableUser && !isReferralModalExcludedPath)

    if (!shouldShow) {
      setShowReferralModal(false)
      return
    }

    const timeoutId = setTimeout(() => {
      setShowReferralModal(true)
    }, 5000)

    return () => clearTimeout(timeoutId)
  }, [shouldShowReferralModal, isReferralModalApplicableUser, isReferralModalExcludedPath, router.pathname])

  // Encyclopedia and drill are intentionally absent: they defer the paywall until interaction
  // (useDeferredPaywallTrigger) so the pages stay indexable.
  const subscriptionPopupIncludedPaths = [
    ROUTES.BOOTCAMP,
    ROUTES.CURRICULUM,
    ROUTES.STUDY_TIPS,
    ROUTES.DIGITAL_TOOL,
    ROUTES.LEADERBOARD,
    ROUTES.MATH_TOPICS,
    ROUTES.MATH_VAULT,
    ROUTES.DASHBOARD,
  ]

  const isPathIncluded = subscriptionPopupIncludedPaths.some(
    (path) => router.pathname.startsWith(path) || router.asPath.startsWith(path),
  )

  // Their family subscription is fine and Dashboard is redirecting them; a paywall here reads as "lapsed"
  const isLeavingDashboardAsFamilyAdmin =
    Boolean(user?.familyInfo?.isFamilyAdmin) &&
    !user?.familyInfo?.hasLicense &&
    (router.pathname.startsWith(ROUTES.DASHBOARD) || router.asPath.startsWith(ROUTES.DASHBOARD))

  const requiresSubscriptionPurchase = !hasValidSubscription && isSubscriptionRequired
  const isPaywallApplicable = (requiresSubscriptionPurchase || showGuestSubModal) && !isLeavingDashboardAsFamilyAdmin
  const showDeferredPaywall = useDeferredPaywallTrigger({ isApplicable: isPaywallApplicable })

  const shouldShowPaywallForPath = isPathIncluded || showDeferredPaywall

  useEffect(() => {
    if (userId && checklistItem) {
      if (!checklistItem.isCompleted) {
        let feature = REWARD_FEATURE.CHECKLIST
        let event_id = `${REWARD_FEATURE.CHECKLIST}#${rewardFeature}`
        if (rewardFeature === REWARD_FEATURE.ONBOARDING) {
          feature = REWARD_FEATURE.ONBOARDING
          event_id = `${REWARD_FEATURE.ONBOARDING}#rewards`
        }

        if (rewardFeature !== REWARD_FEATURE.ONBOARDING) {
          setRewards({
            xp: checklistItem?.xp || 0,
            kudos: 0,
            gems: checklistItem?.gems || 0,
            feature,
            event_id,
          }).then(() => {
            dispatch(notificationApi.util.invalidateTags(['notification']))
            dispatch(leaderboardApi.util.invalidateTags(['leaderboard']))
          })
        }

        checklistAnalytics.logChecklistItemCompleted(rewardFeature)
      } else {
        const timeoutId = setTimeout(
          () => {
            dispatch(unsetChecklistNotification())
          },
          rewardFeature === REWARD_FEATURE.MATH_TASK
            ? 28000
            : rewardFeature === REWARD_FEATURE.ONBOARDING
            ? 14000
            : 10000,
        )
        return () => {
          clearTimeout(timeoutId)
        }
      }
    }
  }, [checklistItem])

  useEffect(() => {
    if (!hasPopUpCoupon && coupon?.couponCode) {
      setShowPopUpCoupon(true)
    } else {
      setShowPopUpCoupon(false)
    }
  }, [hasPopUpCoupon, coupon])

  useEffect(() => {
    if (hasPopUpCoupon) return
    setHasPopUpCoupon(localStorage.getItem('popUpCouponUpdated'))
  }, [hasPopUpCoupon])

  useHasLevelUp()

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (showPopUpCoupon) {
      dispatch(setSubscriptionCoupon(coupon?.couponCode))
    }
  }, [coupon, dispatch, showPopUpCoupon])

  useEffect(() => {
    if (/Android/i.test(navigator.userAgent)) {
      dispatch(setShowAndroidAppModalCreator(true))
    }
  }, [])

  // Payment modals own the screen; the state switch prompt waits until none is up.
  const isPaymentModalOpen =
    (isPaywallApplicable && shouldShowPaywallForPath) ||
    (showPopUpCoupon && Boolean(user?.userCountry) && !appConfig.isSchoolPlatform)

  return (
    <Fragment>
      {homebaseIsOpen && <HomebaseModal />}
      {funfact && <FunfactModal />}
      {storyCardId && <ModalStoryCard storyCardId={storyCardId} />}
      {trophyQuestId && <QuestFinish trophyQuestId={trophyQuestId} />}
      {campaignContent && <SSOCampaignCard campaignContent={campaignContent} />}
      {showTypeform && <TypeformFeedback />}
      {!questState.showCompletedQuestModal && showLevelUp && !isOnboarding && (
        <GamificationModal
          isShowingModal={showLevelUp && rewardFeature !== REWARD_FEATURE.ONBOARDING}
          modalType={ModalType.LEVEL_UP}
          points={rewards.data?.xp}
          questState={questState}
        />
      )}
      {showGemShop && <GemShop />}
      {showUserFeedbackModal && <UserFeedbackModal />}

      {/* Paywall: subscription-required users get the trial variant; guests (not authenticated) in non-subscription
          countries get the no-trial variant. The mobile app shows the access-expired screen instead. */}
      {isPaywallApplicable &&
        shouldShowPaywallForPath &&
        (isMobileApp ? <AccessExpiredMobileApp /> : <SubscriptionModalV1 isTrial={requiresSubscriptionPurchase} />)}
      {showPopUpCoupon && user?.userCountry && !appConfig.isSchoolPlatform && (
        <PilotCountryCouponModal setUpdate={setHasPopUpCoupon} userCountry={user?.userCountry?.toLowerCase()} />
      )}
      {isImageModalOpen && <ChatAiImageModal />}
      {showCurriculumChangeWarning && !appConfig.isSchoolPlatform && (
        <CurriculumChangeWarningModal isOnboarding={isOnboarding} />
      )}
      {useShowMobileAppPopup() && <GetAndroidAppModal />}
      {showCertificateModal && <CurriculumCertificateClaimModal />}
      {/* Before the migration flow: DOM order breaks the z-top tie, so migration paints on top. */}
      <StateSwitchModal isBlocked={isPaymentModalOpen} />
      {/* {showReferralModal && <ReferralModal />} */}
      <ProgressMigrationFlow />
    </Fragment>
  )
}
